import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from './ui/Icon'
import apiService from '../services/api'
import { useGeoCascade } from '../hooks/useGeoCascade'
import SearchableSelect from './ui/SearchableSelect'
import { APP_CONFIG } from '../data/constants.jsx'

/**
 * Edit a member's home address.
 *
 * Bangladesh is the only country the app carries administrative data for, so it
 * is the only one that can offer real pickers: division -> district -> thana,
 * each fetched from /api/salat/geo, followed by a free-text এলাকা (the area
 * under the thana, which no dataset lists). Every other country gets a
 * free-text city, because none of them has a "thana" and the UAE has only
 * emirates — one rigid form cannot fit all twelve. Both free texts are the
 * same field, short_address.
 *
 * Only the thana is sent on save; the server derives district and division from
 * it. That keeps this component from having to keep three ids consistent.
 */
const AddressEditor = ({ user, onSaved, onCancel }) => {
  const { t } = useTranslation()

  const [country, setCountry] = useState(user?.country || '')
  // The এলাকা under the thana in Bangladesh, the city anywhere else.
  const [area, setArea] = useState(user?.short_address || '')

  // Same cascade the signup form uses, seeded with what the member already has.
  const geo = useGeoCascade(country, {
    division: user?.division, district: user?.district, thana: user?.thana,
  })
  const { division, district, thana, divisions, districts, thanas } = geo

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isBD = geo.bd

  const changeCountry = (value) => { setCountry(value); setError('') }

  // Optional everywhere, but a single character is a typo, not a place.
  const text = area.trim()
  const canSave = country.trim().length > 0 && (text.length === 0 || text.length >= 2)
  const incomplete = isBD && !thana

  const save = async () => {
    if (!canSave || saving) return
    setSaving(true); setError('')
    try {
      // Bangladesh sends only the deepest level chosen — district and division
      // are derived server-side. Sending nothing but the country leaves any
      // stored ids untouched, which is why they are cleared explicitly when the
      // member stops short, and cleared entirely elsewhere so a member who moves
      // abroad is not left filed under a Bangladeshi district.
      const levels = isBD
        ? (thana ? { thana }
           : district ? { district, thana: '' }
           : division ? { division, district: '', thana: '' }
           : { division: '', district: '', thana: '' })
        : { division: '', district: '', thana: '' }
      const updated = await apiService.updateProfile({ country, ...levels, short_address: text })
      onSaved?.(updated?.user ?? updated)
    } catch (err) {
      // The server returns field-level messages in Bangla; show them rather than
      // a generic failure, since they say exactly what to correct.
      const detail = err?.data && typeof err.data === 'object'
        ? Object.values(err.data).flat().join(' ')
        : ''
      setError(detail || t('address.saveFailed'))
      setSaving(false)
    }
  }

  const field = 'w-full rounded-lg border border-border bg-card text-foreground px-3 py-2 text-sm'

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-sm text-muted-foreground">{t('address.country')}</span>
        <select className={field} value={country} onChange={(e) => changeCountry(e.target.value)}>
          <option value="">{t('address.selectCountry')}</option>
          {APP_CONFIG.supported.countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>

      {isBD ? (
        <>
          <SearchableSelect
            id="addr-division"
            label={t('address.division')}
            placeholder={t('address.selectDivision')}
            options={divisions}
            value={division}
            onChange={geo.chooseDivision}
            triggerClassName={field}
          />
          <SearchableSelect
            id="addr-district"
            label={t('address.district')}
            placeholder={t('address.selectDistrict')}
            options={districts}
            value={district}
            onChange={geo.chooseDistrict}
            disabled={!division}
            triggerClassName={field}
          />
          <SearchableSelect
            id="addr-thana"
            label={t('address.thana')}
            placeholder={t('address.selectThana')}
            options={thanas}
            value={thana}
            onChange={geo.setThana}
            disabled={!district}
            triggerClassName={field}
          />
          <label className="block">
            <span className="text-sm text-muted-foreground">{t('address.area')}</span>
            <input id="addr-area" className={field} value={area} maxLength={255}
                   onChange={(e) => setArea(e.target.value)}
                   placeholder={t('address.areaPlaceholder')} />
          </label>
        </>
      ) : country ? (
        <label className="block">
          <span className="text-sm text-muted-foreground">{t('address.city')}</span>
          <input className={field} value={area} maxLength={255}
                 onChange={(e) => setArea(e.target.value)}
                 placeholder={t('address.cityPlaceholder')} />
        </label>
      ) : null}

      {incomplete && !error && (
        <p className="text-sm text-muted-foreground flex items-start gap-1.5">
          <Icon name="CircleAlert" className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{t('address.incompleteHint')}</span>
        </p>
      )}

      {error && <p className="text-sm text-red-500" role="alert">{error}</p>}

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={save} disabled={!canSave || saving}
          className="px-4 py-2 bg-gradient-primary text-primary-foreground rounded-lg text-sm font-medium
                     hover:opacity-90 transition-opacity shadow-soft
                     focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
                     disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? t('address.saving') : t('address.save')}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}
          className="px-4 py-2 border border-border text-foreground rounded-lg text-sm font-medium
                     hover:bg-muted transition-colors
                     focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2
                     disabled:opacity-40 disabled:cursor-not-allowed">
          {t('address.cancel')}
        </button>
      </div>
    </div>
  )
}

export default AddressEditor
